import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    BedDouble,
    CalendarRange,
    Globe,
    Users,
    MessageSquare,
    BrushCleaning,
    Wrench,
    Star,
    Sparkles,
    Zap,
    Salad,
    PlugZap,
    UploadCloud,
    BookOpen,
    Infinity as InfinityIcon,
    UserCog,
    Building2,
    Search,
    History,
    DatabaseBackup,
    CircleDot,
    Cpu,
    Map,
    LogOut,
    Settings as SettingsIcon,
    Notebook,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApp } from "@/lib/AppContext";
import { useAuth } from "@/lib/AuthContext";
import { cn } from "@/lib/utils";

const NAV_BY_ROLE = {
    owner: [
        ["Operations", [
            ["/admin/dashboard", LayoutDashboard, "Dashboard"],
            ["/admin/rooms", BedDouble, "Rooms"],
            ["/admin/reservations", CalendarRange, "Reservations"],
            ["/admin/booking", Globe, "Booking Engine"],
            ["/admin/housekeeping", BrushCleaning, "Housekeeping"],
            ["/admin/maintenance", Wrench, "Maintenance"],
            ["/admin/floor-map", Map, "Floor Map"],
            ["/admin/devices", Cpu, "Smart Devices"],
            ["/admin/guests", Users, "Guests"],
        ]],
        ["Engagement", [
            ["/admin/concierge", MessageSquare, "AI Concierge"],
            ["/admin/reviews", Star, "Reviews"],
            ["/admin/copilot", Sparkles, "Staff Copilot"],
        ]],
        ["Intelligence", [
            ["/admin/energy", Zap, "Energy / IoT"],
            ["/admin/food-waste", Salad, "Food Waste"],
            ["/admin/ai-providers", Cpu, "AI Providers"],
            ["/admin/loop", InfinityIcon, "Improvement Loop"],
        ]],
        ["Content", [
            ["/admin/blog", Notebook, "Blog"],
        ]],
        ["Platform", [
            ["/admin/integrations", PlugZap, "Integrations"],
            ["/admin/migration", UploadCloud, "Migration"],
            ["/admin/backup", DatabaseBackup, "Backup"],
            ["/admin/audit", History, "Audit Log"],
            ["/admin/users", UserCog, "Users"],
            ["/admin/strategy", BookOpen, "Architecture"],
            ["/admin/settings", SettingsIcon, "Settings"],
        ]],
    ],
};
NAV_BY_ROLE.manager = NAV_BY_ROLE.owner;

NAV_BY_ROLE.front_desk = [
    ["Operations", [
        ["/admin/dashboard", LayoutDashboard, "Dashboard"],
        ["/admin/reservations", CalendarRange, "Reservations"],
        ["/admin/booking", Globe, "Booking Engine"],
        ["/admin/guests", Users, "Guests"],
        ["/admin/rooms", BedDouble, "Rooms"],
    ]],
    ["Engagement", [
        ["/admin/concierge", MessageSquare, "AI Concierge"],
        ["/admin/reviews", Star, "Reviews"],
        ["/admin/copilot", Sparkles, "Staff Copilot"],
    ]],
    ["Platform", [
        ["/admin/settings", SettingsIcon, "Settings"],
    ]],
];

NAV_BY_ROLE.housekeeping = [
    ["Operations", [
        ["/admin/dashboard", LayoutDashboard, "Dashboard"],
        ["/admin/housekeeping", BrushCleaning, "Housekeeping"],
    ]],
    ["Platform", [
        ["/admin/settings", SettingsIcon, "Settings"],
    ]],
];

NAV_BY_ROLE.maintenance = [
    ["Operations", [
        ["/admin/dashboard", LayoutDashboard, "Dashboard"],
        ["/admin/maintenance", Wrench, "Maintenance"],
    ]],
    ["Platform", [
        ["/admin/settings", SettingsIcon, "Settings"],
    ]],
];

export function AppShell({ children }) {
    const { hotel, view, setView, now, system } = useApp();
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const date = now.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
    const time = now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    const llmHealthy = system?.llm?.healthy !== false && system?.llm?.has_key !== false;
    const mongoOk = system?.db?.ok !== false && system?.mongo?.ok !== false;
    const allOk = llmHealthy && mongoOk;
    const groups = NAV_BY_ROLE[user?.role || "front_desk"] || NAV_BY_ROLE.front_desk;

    const doLogout = () => {
        logout();
        navigate("/login");
    };

    return (
        <div className="min-h-screen bg-background text-foreground">
            <div className="flex min-h-screen">
                <aside
                    className="hidden lg:flex flex-col w-64 shrink-0 border-r border-border bg-card"
                    data-testid="app-sidebar"
                >
                    <div className="flex items-center gap-2 px-5 h-16 border-b border-border">
                        <div className="w-8 h-8 rounded-md bg-primary text-primary-foreground grid place-items-center">
                            <Building2 className="w-4 h-4" />
                        </div>
                        <div>
                            <div className="text-sm font-semibold tracking-tight">HotelOS</div>
                            <div className="text-xs text-muted-foreground">
                                {hotel?.name || "HotelOS"}
                            </div>
                        </div>
                    </div>
                    <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
                        {groups.map(([label, items]) => (
                            <div key={label}>
                                <div className="px-2 mb-1 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                                    {label}
                                </div>
                                <div className="space-y-0.5">
                                    {items.map(([to, Icon, name]) => {
                                        const Active = location.pathname.startsWith(to);
                                        return (
                                            <Link
                                                key={to}
                                                to={to}
                                                data-testid={`sidebar-nav-item-${to.replace(/[^a-z]/g, "")}`}
                                                className={cn(
                                                    "group relative flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm",
                                                    Active
                                                        ? "bg-secondary text-foreground"
                                                        : "text-muted-foreground hover:bg-secondary hover:text-foreground",
                                                )}
                                            >
                                                {Active && (
                                                    <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r bg-primary" />
                                                )}
                                                <Icon className="w-4 h-4" />
                                                <span>{name}</span>
                                            </Link>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </nav>
                    <div className="p-3 border-t border-border">
                        <div className="rounded-lg bg-secondary/60 p-2.5">
                            <div className="text-xs text-muted-foreground">Signed in as</div>
                            <div className="text-sm font-medium truncate">{user?.name || user?.email}</div>
                            <div className="text-[10px] text-muted-foreground capitalize">{user?.role?.replace("_", " ")}</div>
                            <Button
                                size="sm"
                                variant="outline"
                                className="w-full mt-2"
                                onClick={doLogout}
                                data-testid="sidebar-logout"
                            >
                                <LogOut className="w-3.5 h-3.5 mr-1" /> Sign out
                            </Button>
                        </div>
                    </div>
                </aside>

                <div className="flex-1 flex flex-col min-w-0">
                    <header className="h-16 border-b border-border bg-card flex items-center px-4 lg:px-6 gap-3">
                        <Link to="/" className="flex items-center gap-2 text-sm" data-testid="topbar-home-link">
                            <Building2 className="w-4 h-4 text-muted-foreground" />
                            <span className="font-medium">{hotel?.name || "HotelOS"}</span>
                            {hotel?.city && (
                                <span className="text-muted-foreground hidden md:inline">· {hotel.city}</span>
                            )}
                        </Link>
                        <div className="hidden md:flex items-center gap-2 ml-4 flex-1 max-w-md">
                            <div className="relative w-full">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    className="pl-9 bg-secondary border-transparent"
                                    placeholder="Search reservations, guests, rooms"
                                    data-testid="topbar-global-search"
                                />
                            </div>
                        </div>
                        <div className="flex-1 md:hidden" />
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span
                                className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5"
                                title={`DB: ${mongoOk ? "ok" : "down"} · LLM: ${llmHealthy ? "healthy" : "degraded"}`}
                                data-testid="system-status-indicator"
                            >
                                <CircleDot className={`w-3 h-3 ${allOk ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"}`} />
                                <span className="hidden md:inline">{allOk ? "All systems ok" : "Degraded"}</span>
                            </span>
                            <span className="hidden sm:inline tabular">
                                {date} · {time}
                            </span>
                        </div>
                        <div className="flex items-center bg-secondary rounded-lg p-0.5">
                            <Button
                                size="sm"
                                variant={view === "staff" ? "default" : "ghost"}
                                className="h-8 px-3 text-xs"
                                onClick={() => setView("staff")}
                                data-testid="topbar-environment-toggle-staff"
                            >
                                <UserCog className="w-3.5 h-3.5 mr-1" /> Staff
                            </Button>
                            <Button
                                size="sm"
                                variant={view === "guest" ? "default" : "ghost"}
                                className="h-8 px-3 text-xs"
                                onClick={() => setView("guest")}
                                data-testid="topbar-environment-toggle-guest"
                            >
                                <Users className="w-3.5 h-3.5 mr-1" /> Guest
                            </Button>
                        </div>
                    </header>
                    <main className="flex-1 overflow-y-auto">
                        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
                            {children}
                        </div>
                    </main>
                </div>
            </div>
        </div>
    );
}
